import React, { useState, useCallback } from "react";
import {
  Alert,
  Box,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useMutation, useQuery } from "convex/react";
import { useStableQuery } from "../../hooks/useStableQuery";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { useMsal } from "@azure/msal-react";
import { Controller } from "react-hook-form";
import { DataTableShell } from "../../components/table/DataTableShell";
import { EntityFormDialog } from "./components/EntityFormDialog";
import { cardSchema } from "./schemas/cardSchema";
import type { CardFormData } from "./schemas/cardSchema";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Card {
  _id: Id<"cards">;
  number: string;
  description: string;
  isActive: boolean;
  createdAt?: number;
  vendorCount?: number;
}

// ---------------------------------------------------------------------------
// Column definitions — NO delete column per D-12
// ---------------------------------------------------------------------------

function buildColumns(
  onEdit: (card: Card) => void,
): GridColDef<Card>[] {
  return [
    {
      field: "number",
      headerName: "Card Number",
      width: 130,
      sortable: true,
      renderCell: (params: GridRenderCellParams<Card, string>) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          ending in {params.value}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 200,
      sortable: true,
      renderCell: (params: GridRenderCellParams<Card, string>) => (
        <Typography variant="body2" noWrap title={params.value ?? ""}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "_id",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Card>) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton
            size="small"
            aria-label={`Edit card ${params.row.number}`}
            onClick={() => onEdit(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];
}

// ---------------------------------------------------------------------------
// Snackbar state helper
// ---------------------------------------------------------------------------

interface SnackState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

// ---------------------------------------------------------------------------
// CardsAdminPage
// ---------------------------------------------------------------------------

export function CardsAdminPage() {
  // --- MSAL identity (verbatim from AppShell.tsx lines 110-137) ---
  const { instance, accounts } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const account = activeAccount ?? accounts[0];
  const claims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;
  const claimStr = (k: string) =>
    typeof claims[k] === "string" ? (claims[k] as string).trim() : "";
  const email =
    (account?.username && account.username.trim()) ||
    claimStr("preferred_username") ||
    claimStr("upn") ||
    claimStr("email") ||
    "";

  // Admin role check
  const roleEmailCandidates = [
    account?.username ?? "",
    claimStr("preferred_username"),
    claimStr("upn"),
    claimStr("email"),
  ].filter((e) => e && e.includes("@"));
  const userRecord = useQuery(
    api.users.findByAnyEmail,
    roleEmailCandidates.length > 0 ? { emails: roleEmailCandidates } : "skip",
  );
  const isAdmin = userRecord?.role === "admin";

  // --- Data queries (MANDATORY useStableQuery, never raw useQuery) ---
  const cardsRaw = useStableQuery(api.cards.listActive, {}, []);

  const loading = cardsRaw === undefined;
  const cards = (cardsRaw ?? []) as Card[];

  // --- Mutation ---
  const cardUpdate = useMutation(api.cards.update);

  // --- Edit dialog state ---
  const [editingCard, setEditingCard] = useState<Card | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mutationError, setMutationError] = useState<string | undefined>(undefined);

  // --- Snackbar state ---
  const [snack, setSnack] = useState<SnackState>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnack = useCallback(
    (message: string, severity: "success" | "error") => {
      setSnack({ open: true, message, severity });
    },
    [],
  );

  const handleCloseSnack = useCallback(() => {
    setSnack((prev) => ({ ...prev, open: false }));
  }, []);

  // --- Handlers ---
  const handleOpenEdit = useCallback((card: Card) => {
    setEditingCard(card);
    setMutationError(undefined);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingCard(null);
    setMutationError(undefined);
  }, []);

  const handleSubmit = useCallback(
    async (data: CardFormData) => {
      if (!editingCard) return;
      try {
        await cardUpdate({
          id: editingCard._id,
          description: data.description,
          reviewerEmail: userRecord?.email ?? email,
        });
        showSnack("Card updated", "success");
        setDialogOpen(false);
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : "Failed to save card. Please try again.";
        setMutationError(message);
        showSnack(message, "error");   // Mirror Vendor/GL pattern — notify outside dialog
        throw err;                      // Re-throw so EntityFormDialog keeps isSubmitting=true until dialog closes
      }
    },
    [editingCard, email, cardUpdate, showSnack],
  );

  // --- Columns (built once per render; depends on handleOpenEdit) ---
  const COLUMNS = React.useMemo(() => buildColumns(handleOpenEdit), [handleOpenEdit]);

  // --- Fail-closed role gate ---
  if (userRecord === undefined) {
    // Still loading — render nothing (fail-closed during loading)
    return null;
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Administrator role required to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <DataTableShell
        data-tour="page-cards"
        title="Cards"
        subtitle="Credit card descriptions (6 fixed cards — add/remove not available)"
        rows={cards}
        columns={COLUMNS}
        loading={loading}
        emptyTitle="No cards found"
        emptyDescription="Cards are seeded at setup. Contact an administrator if no cards appear."
        itemLabel="card"
        gridProps={{
          initialState: {
            sorting: { sortModel: [{ field: "number", sort: "asc" }] },
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          },
          disableRowSelectionOnClick: true,
        }}
      />

      {/* Edit Dialog — description only, per D-12 / ADMIN-03 */}
      <EntityFormDialog<CardFormData>
        title="Edit Card"
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        schema={cardSchema}
        defaultValues={{ description: editingCard?.description ?? "" }}
        auditHref={
          editingCard ? `/audit?entity=card&id=${editingCard._id}` : undefined
        }
        errorAlert={mutationError}
        renderFields={(control, errors) => (
          <>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Card ending in {editingCard?.number}
            </Typography>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Card Description"
                  autoFocus
                  fullWidth
                  helperText={errors.description?.message}
                  error={!!errors.description}
                />
              )}
            />
          </>
        )}
      />

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={handleCloseSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnack}
          severity={snack.severity}
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CardsAdminPage;
